/**
 * Email Account Server Functions
 *
 * This module provides TanStack Start server functions for email account-related
 * API calls. These functions wrap Orval-generated API functions to provide
 * server-side execution with automatic cookie forwarding via the custom instance.
 */

import { createServerFn } from "@tanstack/react-start";
import type { EmailAccountListResponse, EmailAccountResponse } from "@/lib/api/generated";
import {
  listEmailAccountsApiWorkspacesWorkspaceIdEmailAccountsGet,
  createEmailAccountApiWorkspacesWorkspaceIdEmailAccountsPost,
  suspendEmailAccountApiWorkspacesWorkspaceIdEmailAccountsAccountIdSuspendPost,
  activateEmailAccountApiWorkspacesWorkspaceIdEmailAccountsAccountIdActivatePost,
} from "@/lib/api/generated/email-accounts/email-accounts";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logInfo, logError } from "@/lib/structured-log";
import type { SenderAccountOption } from "@/components/campaign/forms/setting-form";

/**
 * Map EmailAccountResponse to SenderAccountOption
 */
function mapEmailAccountToSenderOption(account: EmailAccountResponse): SenderAccountOption {
  return {
    id: account.id,
    email: account.email,
    name: account.display_name ?? undefined,
    dailyLimit: account.daily_limit,
    dailyUsage: account.sent_today ?? 0,
    disabled: account.status !== "active",
    integrationType: "workus_email",
  };
}

export const listEmailAccountsServer = createServerFn({ method: "GET" })
  .validator((workspaceId: string) => workspaceId)
  .handler(async ({ data: workspaceId }): Promise<EmailAccountListResponse> => {
    logInfo("email_accounts.list.server_fn_called", {
      workspace_id: workspaceId,
    });

    try {
      const response = await listEmailAccountsApiWorkspacesWorkspaceIdEmailAccountsGet(workspaceId);

      if (response.status !== 200) {
        logError("email_accounts.list.failed", {
          status: response.status,
          workspace_id: workspaceId,
        });
        throw new Error(
          JSON.stringify({ detail: "Failed to list email accounts", status: response.status }),
        );
      }

      return response.data;
    } catch (error) {
      logError("email_accounts.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: workspaceId,
      });
      throw error;
    }
  });

/**
 * Suspend email account
 *
 * Server function that suspends an email account in a workspace.
 * Requires authentication and workspace access.
 */
export const suspendEmailAccountServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; accountId: string; reason?: string }) => data)
  .handler(async ({ data }): Promise<EmailAccountResponse> => {
    const reason =
      (data.reason ?? "User requested disconnect").trim() || "User requested disconnect";

    logInfo("email_accounts.suspend.server_fn_called", {
      workspace_id: data.workspaceId,
      account_id: data.accountId,
      reason,
    });

    try {
      const response =
        await suspendEmailAccountApiWorkspacesWorkspaceIdEmailAccountsAccountIdSuspendPost(
          data.workspaceId,
          data.accountId,
          { reason },
        );

      if (response.status !== 200) {
        logError("email_accounts.suspend.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          account_id: data.accountId,
        });
        throw new Error(
          JSON.stringify({ detail: "Failed to suspend email account", status: response.status }),
        );
      }

      logInfo("email_accounts.suspend.success", {
        workspace_id: data.workspaceId,
        account_id: data.accountId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      logError("email_accounts.suspend.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        account_id: data.accountId,
      });
      throw error;
    }
  });

/**
 * Activate email account
 *
 * Server function that activates a suspended email account in a workspace.
 * Requires authentication and workspace access.
 */
export const activateEmailAccountServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; accountId: string }) => data)
  .handler(async ({ data }): Promise<EmailAccountResponse> => {
    logInfo("email_accounts.activate.server_fn_called", {
      workspace_id: data.workspaceId,
      account_id: data.accountId,
    });

    try {
      const response =
        await activateEmailAccountApiWorkspacesWorkspaceIdEmailAccountsAccountIdActivatePost(
          data.workspaceId,
          data.accountId,
        );

      if (response.status !== 200) {
        logError("email_accounts.activate.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          account_id: data.accountId,
        });
        throw new Error(
          JSON.stringify({ detail: "Failed to activate email account", status: response.status }),
        );
      }

      logInfo("email_accounts.activate.success", {
        workspace_id: data.workspaceId,
        account_id: data.accountId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      logError("email_accounts.activate.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        account_id: data.accountId,
      });
      throw error;
    }
  });

/**
 * Create email account
 *
 * Server function that creates a new email account in a workspace.
 * Automatically constructs the email address from username and fixed domain.
 * Requires authentication and workspace access.
 */
export const createEmailAccountServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      username: string;
      displayName?: string;
      domainSuffix?: string; // e.g. "@workus.email"
    }) => data,
  )
  .handler(async ({ data }) => {
    const suffix = (data.domainSuffix ?? "@workus.email").trim();
    const email = `${data.username}${suffix}`;

    logInfo("email_accounts.create.server_fn_called", {
      workspace_id: data.workspaceId,
      username: data.username,
      email,
      has_display_name: !!data.displayName,
    });

    try {
      const response = await createEmailAccountApiWorkspacesWorkspaceIdEmailAccountsPost(
        data.workspaceId,
        {
          email,
          display_name: data.displayName ?? null,
          provider: "resend",
          daily_limit: 30,
        },
      );

      if (response.status !== 201) {
        const errorMessage = extractApiErrorMessage(response, "Failed to create email account");

        let code = "EMAIL_ACCOUNT_CREATE_FAILED";
        let conflictEmail: string | undefined;

        if (typeof errorMessage === "string") {
          const conflictMatch = errorMessage.match(/^Email account already exists:\s*(.+)$/i);
          if (conflictMatch) {
            code = "EMAIL_ACCOUNT_ALREADY_EXISTS";
            conflictEmail = conflictMatch[1];
          } else if (
            errorMessage.toLowerCase().includes("invalid account data") ||
            errorMessage.toLowerCase().includes("invalid email address")
          ) {
            code = "EMAIL_ACCOUNT_INVALID_DATA";
          }
        }

        logError("email_accounts.create.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          email,
          error: errorMessage,
          code,
          conflict_email: conflictEmail,
        });

        throw new Error(
          JSON.stringify({
            code,
            detail: errorMessage,
            ...(conflictEmail ? { email: conflictEmail } : {}),
            status: response.status,
          }),
        );
      }

      logInfo("email_accounts.create.success", {
        account_id: response.data.id,
        workspace_id: data.workspaceId,
        email,
      });

      return mapEmailAccountToSenderOption(response.data);
    } catch (error) {
      if (error instanceof Error) {
        const parsed = (() => {
          try {
            return JSON.parse(error.message) as {
              code?: string;
            };
          } catch {
            return undefined;
          }
        })();
        if (parsed && typeof parsed.code === "string") {
          throw error;
        }
      }

      const errorMessage = extractApiErrorMessage(error, "Failed to create email account");

      let code = "EMAIL_ACCOUNT_CREATE_FAILED";
      let conflictEmail: string | undefined;

      if (typeof errorMessage === "string") {
        const conflictMatch = errorMessage.match(/^Email account already exists:\s*(.+)$/i);
        if (conflictMatch) {
          code = "EMAIL_ACCOUNT_ALREADY_EXISTS";
          conflictEmail = conflictMatch[1];
        } else if (
          errorMessage.toLowerCase().includes("invalid account data") ||
          errorMessage.toLowerCase().includes("invalid email address")
        ) {
          code = "EMAIL_ACCOUNT_INVALID_DATA";
        }
      }

      logError("email_accounts.create.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        username: data.username,
        code,
        conflict_email: conflictEmail,
      });

      throw new Error(
        JSON.stringify({
          code,
          detail: errorMessage,
          ...(conflictEmail ? { email: conflictEmail } : {}),
          status: undefined,
        }),
      );
    }
  });
